import {Component, Inject, OnInit} from '@angular/core';
import {Favorite} from '@models/favorite';
import {AdService} from '@services/ad.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {AlertService} from '@services/alert.service';
import {AiCreditService} from '@services/ai-credit.service';
import {CityService} from '@services/city.service';
import {TrackingService} from '@services/tracking.service';
import {HttpClient} from '@angular/common/http';
import {environment} from '~/environments/environment';

interface AiSuggestion {
    champ: string;
    libelle: string;
    valeur: any;
    valeur_actuelle?: any;
    source?: string;
    raison?: string;
    deja_applique?: boolean;
    selected?: boolean;
}

interface AiAnalysisCache {
    objective: string;
    result: string;
    date: string;
    suggestions?: AiSuggestion[];
}

interface SuggestionSelectOption {
    value: any;
    label: string;
}

@Component({
    selector: 'app-ai-analysis',
    templateUrl: './ai-analysis.component.html',
    styleUrls: ['./ai-analysis.component.scss']
})
export class AiAnalysisComponent implements OnInit {

    favorite: Favorite;
    step: 'intro' | 'saving' | 'loading' | 'result' | 'rating' = 'intro';
    mode: 'ad' | 'dpe' = 'ad';
    objective: string = '';
    refAdeme: string = '';
    isShare: boolean = false;
    resultHtml: string = '';
    caches: { [mode: string]: AiAnalysisCache } = {ad: null, dpe: null};
    suggestions: AiSuggestion[] = [];
    suggestionsExpanded = false;

    blockedKind: '' | 'credit' | 'plan' = '';
    blockedMessage = '';

    rating = 0;
    comment = '';
    ratingSubmitted = false;

    objectives = [
        {value: 'rp', label: 'Résidence principale', icon: 'fa-home'},
        {value: 'invest', label: 'Investissement locatif', icon: 'fa-key'},
        {value: 'patrim', label: 'Investissement patrimonial', icon: 'fa-building'},
        {value: 'mdb', label: 'Marchand de biens', icon: 'fa-briefcase'},
        {value: 'lcd', label: 'LCD / Airbnb', icon: 'fa-calendar'},
    ];

    constructor(
        private adService: AdService,
        private cityService: CityService,
        public aiCreditService: AiCreditService,
        public trackingService: TrackingService,
        private http: HttpClient,
        public dialogRef: MatDialogRef<AiAnalysisComponent>,
        private alertService: AlertService,
        @Inject(MAT_DIALOG_DATA) public data: any) {
    }

    ngOnInit(): void {
        this.favorite = this.data.favorite;
        this.isShare = this.data.isShare;
        this.refAdeme = this.favorite.ref_ademe ? this.favorite.ref_ademe : '';
        this.loadCachedAnalysis();
        this.refreshBlocked();

        this.dialogRef.disableClose = true;
        this.dialogRef.backdropClick().subscribe(() => this.onNoClick());

        this.trackingService.click('ai_analysis_open', this.analysisId());
    }

    get cachedAnalysis(): AiAnalysisCache {
        return this.caches[this.mode];
    }

    get hasSuggestions(): boolean {
        return this.suggestions.length > 0;
    }

    get canApplySuggestions(): boolean {
        return !this.isShare && this.suggestions.some(suggestion => suggestion.selected);
    }

    get selectedSuggestionsCount(): number {
        return this.suggestions.filter(suggestion => suggestion.selected).length;
    }

    getSuggestionSelectOptions(champ: string): SuggestionSelectOption[] | null {
        switch (champ) {
            case 'furnish':
                return [
                    {value: 0, label: 'Tous confondus'},
                    {value: 1, label: 'Meublé'},
                    {value: 2, label: 'Nu'},
                    {value: 3, label: 'Location courte durée'},
                    {value: 4, label: 'Colocation'},
                ];
            case 'estate_type':
                return [
                    {value: 'Maison', label: 'Maison'},
                    {value: 'Appartement', label: 'Appartement'},
                    {value: 'Chambre', label: 'Chambre (colocation)'},
                    {value: 'Parking', label: 'Parking / Garage'},
                    {value: 'Commerce', label: 'Local commercial'},
                    {value: 'Parties communes', label: 'Parties communes'},
                    {value: 'Terrain', label: 'Terrain'},
                    {value: 'Autres', label: 'Autre'},
                ];
            case 'advice_rent_choice':
                return Object.keys(this.cityService.rentFields)
                    .map(value => ({value, label: this.cityService.rentFields[value].lib}));
            case 'advice_price_choice':
                return Object.keys(this.cityService.priceFields)
                    .map(value => ({value, label: this.cityService.priceFields[value].lib}));
            case 'works_help': {
                const user = this.adService.authenticationService.currentUser;
                return [
                    {value: 0, label: 'Aucun travaux'},
                    {value: user.work_1, label: 'Rafraîchissement (' + user.work_1 + ' €/m²)'},
                    {value: user.work_2, label: 'Rénovation (' + user.work_2 + ' €/m²)'},
                    {value: user.work_3, label: 'Remise à neuf (' + user.work_3 + ' €/m²)'},
                    {value: -1, label: 'Personnaliser'},
                ];
            }
        }
        return null;
    }

    getSuggestionInputType(value: any): string {
        return typeof value === 'number' ? 'number' : 'text';
    }

    get canAnalyze(): boolean {
        if (this.blockedKind !== '') {
            return false;
        }
        if (this.mode === 'dpe') {
            return this.isShare ? !!this.favorite.ref_ademe : this.refAdeme.trim() !== '';
        }
        return !!this.objective;
    }

    storageKey(mode: string): string {
        // Depuis /annonces, le favori n'est pas encore présent dans une liste.
        // On utilise donc l'identifiant de route transmis par la page, qui est
        // stable même si l'objet chargé n'expose pas encore son id habituel.
        return 'lybox_ai_' + (mode === 'dpe' ? 'dpe_' : '') + this.analysisId();
    }

    loadCachedAnalysis(): void {
        ['ad', 'dpe'].forEach(mode => {
            const stored = localStorage.getItem(this.storageKey(mode));
            if (!stored) {
                return;
            }
            try {
                this.caches[mode] = JSON.parse(stored);
            } catch (e) {
                this.caches[mode] = null;
            }
        });

        if (this.caches.ad === null && this.caches.dpe !== null) {
            this.mode = 'dpe';
        }
        this.showCachedResult();
    }

    selectMode(mode: 'ad' | 'dpe'): void {
        if (this.mode === mode || this.step === 'loading' || this.step === 'rating') {
            return;
        }
        this.mode = mode;
        this.showCachedResult();
    }

    selectObjective(value: string): void {
        this.objective = value;
    }

    analyze(): void {
        if (!this.canAnalyze) {
            return;
        }

        const ref = this.refAdeme.trim();
        if (this.mode === 'dpe' && !this.isShare && ref !== (this.favorite.ref_ademe ? this.favorite.ref_ademe : '')) {
            this.step = 'loading';
            this.favorite.ref_ademe = ref;
            this.adService.saveFavorite(this.favorite).subscribe(
                () => this.runAnalysis(),
                () => {
                    this.alertService.error('Une erreur est survenue lors de l\'enregistrement du numéro ADEME.');
                    this.step = 'intro';
                }
            );
            return;
        }

        this.runAnalysis();
    }

    newAnalysis(): void {
        // Chaque analyse peut être notée : on repart d'un avis vierge.
        this.rating = 0;
        this.comment = '';
        this.ratingSubmitted = false;
        this.step = 'intro';
    }

    toggleSuggestions(): void {
        this.suggestionsExpanded = !this.suggestionsExpanded;
    }

    ignoreSuggestions(): void {
        this.suggestions = [];
        this.suggestionsExpanded = false;
        this.persistSuggestions();
    }

    applySuggestions(relaunch = false): void {
        const selectedSuggestions = this.suggestions.filter(suggestion => suggestion.selected);
        if (this.isShare || selectedSuggestions.length === 0) {
            return;
        }

        const fieldSuggestions = selectedSuggestions.filter(suggestion => suggestion.champ !== 'split_lot');
        const unsupportedSuggestions = fieldSuggestions.filter(suggestion =>
            !this.adService.isAiSuggestionEditable(suggestion.champ)
        );
        if (unsupportedSuggestions.length > 0) {
            this.alertService.error('Certaines suggestions ne peuvent pas être appliquées à ce projet.');
            return;
        }
        const splitLots = selectedSuggestions.some(suggestion => suggestion.champ === 'split_lot' && suggestion.valeur === true);
        // La séparation doit précéder les montants agrégés : ceux-ci sont alors
        // bien répartis dans les nouveaux lots, comme lors d'une saisie manuelle.
        if (splitLots) {
            this.favorite = this.adService.splitFavoriteLots(this.favorite);
        }
        fieldSuggestions.forEach(suggestion =>
            this.adService.applyAiSuggestion(this.favorite, suggestion.champ, suggestion.valeur)
        );

        // Recalcule les champs dérivés avant le PUT (rendements, charges,
        // totaux de lots, mensualité…). Aucun de ces champs n'est une saisie IA.
        this.favorite = this.adService.updateValues(this.favorite);
        this.step = 'saving';
        this.adService.saveFavorite(this.favorite).subscribe(
            result => {
                // Le PUT peut renvoyer le favori recalculé ; on le conserve pour la suite du parcours.
                if (result && result.id) {
                    Object.assign(this.favorite, result);
                }
                if (relaunch) {
                    this.runAnalysis();
                    return;
                }
                this.suggestions = this.suggestions.filter(suggestion => !suggestion.selected);
                this.persistSuggestions();
                this.suggestionsExpanded = this.hasSuggestions;
                this.step = 'result';
                this.track('suggestions_apply', String(selectedSuggestions.length));
                this.alertService.success('Modifications appliquées, l’analyse IA prendra en compte les modifications à la prochaine analyse.');
            },
            () => {
                this.alertService.error('Une erreur est survenue lors de l\'enregistrement des modifications proposées.');
                this.step = 'result';
            }
        );
    }

    // Le solde seul ne suffit pas : un essai terminé laisse ses crédits non consommés en base.
    refreshBlocked(): void {
        if (!this.aiCreditService.hasPlan) {
            this.blockedKind = 'plan';
            this.blockedMessage = 'Un abonnement est nécessaire pour lancer une analyse IA.';
        } else if (this.aiCreditService.credit <= 0) {
            this.blockedKind = 'credit';
            this.blockedMessage = 'Vous n\'avez plus de crédit d\'analyse IA.';
        } else {
            this.blockedKind = '';
            this.blockedMessage = '';
        }
    }

    getObjectiveLabel(value: string): string {
        const obj = this.objectives.find(o => o.value === value);
        return obj ? obj.label : value;
    }

    askRating(): void {
        this.rating = 0;
        this.comment = '';
        this.step = 'rating';
    }

    backToResult(): void {
        this.step = 'result';
    }

    onRatingChange(event): void {
        this.rating = event.rating;
    }

    submitRating(): void {
        this.http.post<any>(`${environment.apiUrl}app/user/rate-ai`, {
            rating: this.rating,
            comment: this.comment,
            objective: this.mode === 'dpe' ? '' : this.objective,
            type: this.mode,
            ref_ademe: this.mode === 'dpe' ? this.refAdeme.trim() : '',
        }).subscribe(() => {}, () => {});
        this.track('rating', String(this.rating));
        this.ratingSubmitted = true;
    }

    onNoClick(): void {
        this.dialogRef.close(false);
    }

    private showCachedResult(): void {
        this.ratingSubmitted = false;
        const cached = this.caches[this.mode];
        if (cached) {
            this.resultHtml = cached.result;
            this.suggestions = this.normalizeSuggestions(cached.suggestions);
            this.suggestionsExpanded = false;
            if (this.mode === 'ad') {
                this.objective = cached.objective;
            }
            this.step = 'result';
        } else {
            this.resultHtml = '';
            this.suggestions = [];
            this.suggestionsExpanded = false;
            this.step = 'intro';
        }
    }

    private runAnalysis(): void {
        this.step = 'loading';
        const objective = this.mode === 'dpe' ? '' : this.objective;

        this.track('start');

        const request = this.mode === 'dpe'
            ? this.adService.analyseDpe(this.refAdeme.trim())
            : this.adService.analyzeWithAI(
                (this.isShare == true ? this.favorite.public_id : this.favorite.id), objective, this.mode
            );

        request.subscribe(
            result => {
                this.aiCreditService.applyResponse(result);
                this.refreshBlocked();
                const cached = {
                    objective: objective,
                    result: result.result,
                    date: new Date().toISOString(),
                    suggestions: this.normalizeSuggestions(result.suggestions),
                };
                localStorage.setItem(this.storageKey(this.mode), JSON.stringify(cached));
                this.caches[this.mode] = cached;
                this.resultHtml = result.result;
                this.suggestions = cached.suggestions;
                this.suggestionsExpanded = false;
                this.rating = 0;
                this.comment = '';
                this.ratingSubmitted = false;
                this.step = 'result';
                this.track('success');
            },
            error => {
                const aiError = this.aiCreditService.handleError(error);
                this.step = 'intro';
                this.track('error_' + aiError.kind);
                if (aiError.kind === 'ignore') {
                    return;
                }
                if (aiError.kind === 'credit' || aiError.kind === 'plan') {
                    this.blockedKind = aiError.kind;
                    this.blockedMessage = aiError.message;
                    return;
                }
                this.refreshBlocked();
                this.alertService.error(aiError.message);
            }
        );
    }

    // Un événement par mode d'analyse (ai_analysis_ad_* / ai_analysis_dpe_*) : les deux
    // analyses n'ont ni le même coût ni le même parcours, on veut pouvoir les suivre à part.
    private track(event: string, data: string = ''): void {
        const detail = data !== '' ? data : (this.mode === 'dpe' ? this.refAdeme.trim() : this.objective);
        this.trackingService.click('ai_analysis_' + this.mode + '_' + event, this.analysisId() + ' ' + detail);
    }

    private analysisId(): string {
        return String(this.data.analysisStorageId || this.favorite.id || this.favorite.public_id || this.favorite.ad_id || '');
    }

    private normalizeSuggestions(suggestions: any): AiSuggestion[] {
        if (!Array.isArray(suggestions)) {
            return [];
        }
        return suggestions
            // Une suggestion déjà présente dans le favori n'est pas une incohérence.
            .filter(suggestion => suggestion && suggestion.champ && suggestion.deja_applique !== true)
            .map(suggestion => Object.assign({}, suggestion, {selected: suggestion.selected !== false}));
    }

    private persistSuggestions(): void {
        const cached = this.caches[this.mode];
        if (!cached) {
            return;
        }
        cached.suggestions = this.suggestions;
        localStorage.setItem(this.storageKey(this.mode), JSON.stringify(cached));
    }
}
